import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import ScheduleTableV1 from '../components/tables/ScheduleTableV1';
import {
  GET_SCHEDULE_LIST_V1,
  GET_DRIVER_LIST,
  GET_DRIVER_LIST_V1,
  ASSIGN_DRIVER_V1,
  GET_ALL_PAYER,
  GET_SCHEPASSENGER_LIST,
} from '../constant';
import axios from 'axios';
import { currentDateFormat } from '../utils';
import Swal from '../messageBox/customSweetAlert';
import { useDispatch, useSelector } from 'react-redux';
import { setLoading } from '../redux/loaderAction';
import Loader from '../components/loader/Loader';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';
import SearchComponent from '../components/SearchComponent';
import { useLocation, useNavigate } from 'react-router-dom';

const PAGE_LIMIT = 10;

const parseQueryDate = (value) => {
  if (value == null || String(value).trim() === '') return null;
  const parsed = moment(value, ['YYYY-MM-DD', 'MM/DD/YYYY'], true);
  return parsed.isValid() ? parsed.toDate() : null;
};

const ScheduleListV1 = () => {
  const dispatch = useDispatch();
  const loading = useSelector((state) => state.loader.loading);
  const location = useLocation();
  const navigate = useNavigate();
  const skipQueryFetchRef = useRef(false);
  const scheduleIdFromQuery = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const fromQuery = searchParams.get('selectedScheduleId');
    const fromState = location.state?.selectedScheduleId;
    const raw = fromState ?? fromQuery;
    if (raw == null || String(raw).trim() === '') return null;
    return String(raw).trim();
  }, [location.search, location.state]);

  const fromDateFromQuery = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return parseQueryDate(
      searchParams.get('from') ?? searchParams.get('from_date'),
    );
  }, [location.search]);

  const endDateFromQuery = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return parseQueryDate(searchParams.get('end') ?? searchParams.get('to_date'));
  }, [location.search]);

  const [scheduleList, setScheduleList] = useState([]);
  const [driverList, setDriverList] = useState([]);
  const [currentDate] = useState(new Date());
  const [selectedDriverId, setSelectedDriverId] = useState();
  const [selectedScheduleId, setSelectedScheduleId] = useState();
  const [selectedScheduleTransId, setSelectedScheduleTransId] = useState();
  const [isModalVisible, setModalVisible] = useState(false);
  const [filterValue, setFilterValue] = useState('0');
  const [pmiNo, setPmiNo] = useState('');
  const [payer, setPayer] = useState('0');
  const [fromDate, setFromDate] = useState(new Date());
  const [toDate, setToDate] = useState(new Date());
  const [payerList, setPayerList] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [selectedPassenger, setSelectedPassenger] = useState(null);
  const [passengerList, setPassengerList] = useState([]);
  const [isClicked, setIsClicked] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    recordsPerPage: 10,
  });

  const getPassengerIdForApi = useCallback(() => {
    const v = selectedPassenger?.value;
    if (!v) return '';
    return String(v.passenger?.passenger_id ?? v.passenger_id ?? v.user_id ?? '');
  }, [selectedPassenger]);

  const getDriverIdForApi = useCallback(() => {
    const v = selectedDriver?.value;
    if (!v) return '';
    return String(v.user_id ?? v.driver_id ?? '');
  }, [selectedDriver]);

  const buildSearchQueryParams = useCallback(() => {
    const params = new URLSearchParams();
    const passengerId = getPassengerIdForApi();
    const driverId = getDriverIdForApi();

    if (passengerId) params.set('passenger_id', passengerId);
    if (driverId) params.set('driver_id', driverId);
    if (pmiNo != null && String(pmiNo).trim() !== '') {
      params.set('pmi_no', String(pmiNo).trim());
    }
    if (payer != null && payer !== '' && payer !== '0') {
      params.set('payer_id', String(payer));
    }
    if (fromDate) {
      params.set('from_date', moment(fromDate).format('YYYY-MM-DD'));
    }
    if (toDate) {
      params.set('to_date', moment(toDate).format('YYYY-MM-DD'));
    }
    if (filterValue && filterValue !== '0') {
      params.set('assigned_or_not', filterValue);
    }

    return params;
  }, [
    getPassengerIdForApi,
    getDriverIdForApi,
    pmiNo,
    payer,
    fromDate,
    toDate,
    filterValue,
  ]);

  const fetchSchedulesPage = useCallback(
    async (page = 1, opts = {}) => {
      const fv = opts.filterValue != null ? opts.filterValue : filterValue;
      dispatch(setLoading(true));
      try {
        const fromDateForApi = opts.fromDate ?? fromDate;
        const toDateForApi = opts.toDate ?? toDate;
        const params = {
          page,
          limit: PAGE_LIMIT,
          from_date: fromDateForApi ? moment(fromDateForApi).format('YYYY-MM-DD') : '',
          to_date: toDateForApi ? moment(toDateForApi).format('YYYY-MM-DD') : '',
          passenger_id: getPassengerIdForApi(),
          driver_id: getDriverIdForApi(),
          pmi_no: pmiNo || '',
          payer_id: payer === '0' ? '' : String(payer),
        };
        if (fv && fv !== '0') {
          params.assigned_or_not = fv;
        }
        const sid =
          opts.scheduleId !== undefined ? opts.scheduleId : scheduleIdFromQuery;
        if (sid != null && String(sid).trim() !== '') {
          params.schedule_id = String(sid).trim();
        }

        const response = await axios.get(GET_SCHEDULE_LIST_V1, { params });
        const ok = response?.data?.status === 200 || response?.data?.status === '200';
        if (response?.status === 200 && ok) {
          const rows = Array.isArray(response.data.data) ? response.data.data : [];
          setScheduleList(rows);
          const p = response.data.pagination;
          if (p) {
            setPagination({
              currentPage: p.currentPage ?? page,
              totalPages: Math.max(1, p.totalPages ?? 1),
              totalRecords: p.totalRecords ?? rows.length,
              recordsPerPage: p.recordsPerPage ?? params.limit,
            });
          } else {
            setPagination((prev) => ({
              ...prev,
              currentPage: page,
              totalPages: 1,
              totalRecords: rows.length,
            }));
          }
        } else {
          setScheduleList([]);
          Swal.fire('Info', response?.data?.message || 'No data found', 'info');
        }
      } catch (error) {
        console.error('Error fetching schedules (v1):', error);
        setScheduleList([]);
        Swal.fire('Error', 'Something went wrong while loading schedules', 'error');
      } finally {
        dispatch(setLoading(false));
      }
    },
    [
      dispatch,
      filterValue,
      fromDate,
      toDate,
      getPassengerIdForApi,
      getDriverIdForApi,
      pmiNo,
      payer,
      scheduleIdFromQuery,
    ]
  );

  useEffect(() => {
    const loadUser = async () => {
      const [r2, r3, r4] = await Promise.all([
        axios.get(GET_DRIVER_LIST_V1),
        axios.get(GET_ALL_PAYER),
        axios.get(GET_SCHEPASSENGER_LIST),
      ]);
      setPayerList(r3.data);
      setDriverList(r2.data);
      setPassengerList(r4.data);
    };
    loadUser();
  }, []);

  useEffect(() => {
    if (fromDateFromQuery) setFromDate(fromDateFromQuery);
    if (endDateFromQuery) setToDate(endDateFromQuery);
  }, [fromDateFromQuery, endDateFromQuery]);

  useEffect(() => {
    if (skipQueryFetchRef.current) {
      skipQueryFetchRef.current = false;
      return;
    }
    fetchSchedulesPage(1, {
      scheduleId: scheduleIdFromQuery,
      fromDate: fromDateFromQuery ?? undefined,
      toDate: endDateFromQuery ?? undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch when URL query filters change
  }, [scheduleIdFromQuery, fromDateFromQuery, endDateFromQuery]);

  const driverSaveForm = async () => {
    setIsClicked(true);
    try {
      if (!selectedDriverId) {
        setIsClicked(false);
        Swal.fire({
          title: 'Failed!',
          text: 'Please select a driver',
          icon: 'error',
          confirmButtonText: 'OK',
        });
        return;
      }
      if (selectedScheduleTransId == null || selectedScheduleTransId === '') {
        setIsClicked(false);
        Swal.fire({
          title: 'Failed!',
          text: 'Missing trip leg (schedule trans id). Please try again.',
          icon: 'error',
          confirmButtonText: 'OK',
        });
        return;
      }
      const response = await axios.patch(
        `${ASSIGN_DRIVER_V1}/${selectedScheduleTransId}`,
        { driverId: selectedDriverId }
      );
      const ok =
        response?.data?.status === '200' ||
        response?.data?.status === 200 ||
        response?.status === 200;
      if (ok) {
        setIsClicked(false);
        Swal.fire({
          title: 'Success!',
          text: response.data?.message || 'Driver assigned successfully',
          icon: 'success',
          confirmButtonText: 'OK',
        });
        const assignDriver = driverList.find((driver) => driver.user_id === selectedDriverId);
        const driverObj = assignDriver
          ? {
              firstName: assignDriver.first_name,
              middleName: assignDriver.middle_name,
              lastName: assignDriver.last_name,
            }
          : null;
        setSelectedDriverId();
        setSelectedScheduleTransId();
        setModalVisible(false);

        const transIdStr = String(selectedScheduleTransId);
        setScheduleList((prev) =>
          prev.map((item) => {
            if (item.schedule_id !== selectedScheduleId || !driverObj) return item;
            const transData = Array.isArray(item.transData)
              ? item.transData.map((t) =>
                  String(t.schedule_trans_id) === transIdStr
                    ? { ...t, driver: { ...driverObj } }
                    : t
                )
              : item.transData;
            const sorted = Array.isArray(transData)
              ? [...transData].sort(
                  (a, b) => (a?.schedule_trans_id ?? 0) - (b?.schedule_trans_id ?? 0),
                )
              : [];
            return {
              ...item,
              driver: sorted[0]?.driver ?? item.driver,
              transData,
            };
          })
        );
      } else {
        setIsClicked(false);
        Swal.fire({
          title: 'Failed!',
          text: response.data?.message || 'Assignment failed',
          icon: 'Warning',
          confirmButtonText: 'OK',
        });
      }
    } catch (error) {
      setIsClicked(false);
      console.error('Assign driver error:', error);
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        'Something went wrong while assigning the driver';
      Swal.fire({ title: 'Error', text: msg, icon: 'error', confirmButtonText: 'OK' });
    }
  };

  const handleStatusChange = (e) => {
    const newStatus = e.target.value;
    setFilterValue(newStatus);
    setPagination((p) => ({ ...p, currentPage: 1 }));
    fetchSchedulesPage(1, { filterValue: newStatus });
  };

  const handlePMIChange = (e) => {
    setPmiNo(e.target.value);
  };

  const handlePayerChange = (e) => {
    setPayer(e.target.value);
  };

  const handleSearch = async () => {
    if (errors.from_date || errors.to_date) {
      Swal.fire('Error', 'Please correct the date errors before searching', 'error');
      return false;
    }
    setIsSearchLoading(true);
    setPagination((p) => ({ ...p, currentPage: 1 }));
    try {
      const searchParams = buildSearchQueryParams();
      const queryString = searchParams.toString();
      skipQueryFetchRef.current = true;
      navigate(
        {
          pathname: location.pathname,
          search: queryString ? `?${queryString}` : '',
        },
        { replace: true },
      );
      await fetchSchedulesPage(1, { scheduleId: '' });
    } finally {
      setIsSearchLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'];
    const isNumberKey = /^[0-9]$/.test(e.key);
    const isCtrlC = e.ctrlKey && e.key.toLowerCase() === 'c';
    const isCtrlV = e.ctrlKey && e.key.toLowerCase() === 'v';
    if (
      (!isNumberKey && !allowedKeys.includes(e.key) && !isCtrlC && !isCtrlV) ||
      (e.target.value.length >= 8 && !allowedKeys.includes(e.key) && !isCtrlC && !isCtrlV)
    ) {
      e.preventDefault();
    }
  };

  const driverListWithFullName = driverList.map((driver) => {
    const fullName = `${driver.first_name || ''} ${driver.middle_name || ''} ${driver.last_name || ''}`
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
    return { ...driver, full_name: fullName };
  });

  const passengerListWithFullName = passengerList.map((passenger) => {
    const fullName = `${passenger.first_name || ''} ${passenger.middle_name || ''} ${passenger.last_name || ''} ${passenger.passenger?.pass_pmi_no || ''}`
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
    return { ...passenger, full_name: fullName };
  });

  const onPageChange = (page) => {
    setPagination((p) => ({ ...p, currentPage: page }));
    fetchSchedulesPage(page);
  };

  const onRefreshAfterDelete = () => {
    fetchSchedulesPage(pagination.currentPage);
  };

  return (
    <>
      <section>
        <div className="d-flex justify-content-between form-container" style={{ paddingLeft: '0px', paddingRight: '0px' }}>
          <div>
            <h2 className="fs-5 font-weight-600">Schedule List</h2>
          </div>
          <div>
            <span>{currentDateFormat(currentDate)}</span>
          </div>
        </div>
      </section>
      <section className="filter-section">
        <div className="d-flex justify-content-between filter-container" style={{ background: 'rgb(205 228 218 / 29%)' }}>
          <div className="schedule-filter">
            <div className="me-2">
              <label style={{ display: 'block' }}>Assigned Driver</label>
              <select className="form-select" name="assigned_or_not" value={filterValue} onChange={handleStatusChange}>
                <option value="0">All</option>
                <option value="1">Assigned</option>
                <option value="2">Unassigned</option>
              </select>
            </div>
            <div className="me-2">
              <SearchComponent
                data={passengerListWithFullName}
                searchFields={['full_name']}
                placeholder="Search Passenger"
                label="Passenger"
                onSelect={(p) => setSelectedPassenger(p)}
              />
            </div>
            <div className="me-2">
              <SearchComponent
                data={driverListWithFullName}
                searchFields={['full_name']}
                placeholder="Search Driver"
                label="Driver"
                onSelect={(d) => setSelectedDriver(d)}
              />
            </div>
            <div className="me-2">
              <label style={{ display: 'block' }}>PMI No.</label>
              <input
                type="number"
                name="pmi_no"
                className="form-control"
                placeholder="Search PMI"
                value={pmiNo}
                onChange={handlePMIChange}
                onKeyDown={handleKeyDown}
              />
            </div>
            <div className="me-2">
              <label style={{ display: 'block' }}>Payor</label>
              <select className="form-select" name="payer" value={payer} onChange={handlePayerChange}>
                <option value="0">All</option>
                {payerList.map((py, index) => (
                  <option key={index} value={py.id}>
                    {py.payer_name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <div className="me-2 date-container">
                <label style={{ display: 'block' }}>From</label>
                <DatePicker
                  selected={fromDate}
                  onChange={(date) => {
                    if (toDate && date > toDate) {
                      setErrors({ from_date: 'From date greater than To date' });
                    } else {
                      setErrors({});
                    }
                    setFromDate(date);
                  }}
                  dateFormat="MM/dd/yyyy"
                  autoComplete="off"
                  className="datepicker form-control hasDatepicker"
                  id="from_date_v1"
                  name="from_date"
                />
              </div>
              <div className="error-message" style={{ maxWidth: '100%' }}>
                {errors.from_date && (
                  <span
                    style={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: 'inline-block',
                    }}
                    className="errors"
                  >
                    {errors.from_date}
                  </span>
                )}
              </div>
            </div>
            <div>
              <div className="me-2">
                <label style={{ display: 'block' }}>To</label>
                <DatePicker
                  selected={toDate}
                  onChange={(date) => {
                    if (fromDate && date < fromDate) {
                      setErrors({ to_date: 'To date less than From date' });
                    } else {
                      setErrors({});
                    }
                    setToDate(date);
                  }}
                  dateFormat="MM/dd/yyyy"
                  autoComplete="off"
                  className="datepicker form-control hasDatepicker"
                  id="to_date_v1"
                  name="to_date"
                />
              </div>
              <div className="error-message" style={{ maxWidth: '100%' }}>
                {errors.to_date && (
                  <span
                    className="errors"
                    style={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: 'inline-block',
                    }}
                  >
                    {errors.to_date}
                  </span>
                )}
              </div>
            </div>
            <div>
              <div>
                <button
                  className="btn btn-primary rounded-1"
                  type="button"
                  style={{
                    background: '#31876f',
                    border: 'unset',
                    height: '38px',
                    color: '#fff',
                    padding: '7px',
                  }}
                  onClick={handleSearch}
                  disabled={isSearchLoading}
                >
                  {isSearchLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      Searching...
                    </>
                  ) : (
                    'Search'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section>
        <div className="create-schedule-table" style={{ padding: '10px' }}>
          <div className="table-responsive">
            <ScheduleTableV1
              tableData={scheduleList}
              setTableData={setScheduleList}
              diverList={driverList}
              setSelectedDriverId={setSelectedDriverId}
              selectedDriverId={selectedDriverId}
              onSave={driverSaveForm}
              setSelectedScheduleId={setSelectedScheduleId}
              setSelectedScheduleTransId={setSelectedScheduleTransId}
              setModalVisible={setModalVisible}
              isModalVisible={isModalVisible}
              isClicked={isClicked}
              pagination={pagination}
              onPageChange={onPageChange}
              onRefreshAfterDelete={onRefreshAfterDelete}
            />
          </div>
        </div>
      </section>
      {loading && <Loader />}
    </>
  );
};

export default ScheduleListV1;
